import React, { useState, useEffect } from "react";

type GiftGridVisualProps = {
	readonly width: number;
	readonly height: number;
};

function GiftGridVisual({ width, height }: GiftGridVisualProps) {
	// Calculate primary container size based on original gift dimensions
	const originalMaxDimension = Math.max(width, height);
	const baseContainerSize = Math.min(
		Math.max(120, originalMaxDimension * 25),
		300,
	); // Larger container for extreme aspect ratios
	const [currentConfig, setCurrentConfig] = useState({ width, height });
	const [isAnimating, setIsAnimating] = useState(false);

	// Generate all possible configurations with the same total area
	const totalCells = width * height;
	const possibleConfigs = React.useMemo(() => {
		const configs = [];
		for (let w = 1; w <= totalCells; w++) {
			if (totalCells % w === 0) {
				const h = totalCells / w;
				// Filter out extreme aspect ratios (1xN or Nx1) and very large dimensions
				if (w > 1 && h > 1 && w <= 12 && h <= 12) {
					configs.push({ width: w, height: h });
				}
			}
		}
		// Sort by aspect ratio difference from original to make transitions smoother
		const originalRatio = width / height;
		return configs.sort((a, b) => {
			const ratioA = Math.abs(a.width / a.height - originalRatio);
			const ratioB = Math.abs(b.width / b.height - originalRatio);
			return ratioA - ratioB;
		});
	}, [width, height, totalCells]);

	// Animation effect
	useEffect(() => {
		if (possibleConfigs.length <= 1) return;

		let configIndex = 0;
		const interval = setInterval(() => {
			setIsAnimating(true);
			setTimeout(() => {
				configIndex = (configIndex + 1) % possibleConfigs.length;
				setCurrentConfig(possibleConfigs[configIndex]);
				setIsAnimating(false);
			}, 150); // Brief pause for transition effect
		}, 2000); // Change every 2 seconds

		return () => clearInterval(interval);
	}, [possibleConfigs]);

	const { width: currentWidth, height: currentHeight } = currentConfig;
	const maxSize = Math.max(currentWidth, currentHeight);
	// Smart scaling that adapts to the grid configuration
	const maxGridSize = baseContainerSize - 32; // Leave room for padding
	const scale = Math.min(
		maxGridSize / maxSize,
		20,
		maxGridSize / Math.max(currentWidth, currentHeight),
	);
	const gridWidth = currentWidth * scale;
	const gridHeight = currentHeight * scale;

	// Primary container is fixed size based on original gift
	const primaryContainerSize = baseContainerSize;

	// Calculate secondary container (white) to ensure equal spacing around grid
	const gridAspectRatio = currentWidth / currentHeight;
	const secondaryPadding = 12; // Reduced padding around grid within white container

	let secondaryWidth, secondaryHeight;
	if (gridAspectRatio > 1) {
		// Wide grid - white container expands horizontally, allow overflow if needed
		secondaryWidth = gridWidth + secondaryPadding * 2;
		secondaryHeight = gridHeight + secondaryPadding * 2;
	} else if (gridAspectRatio < 1) {
		// Tall grid - white container expands vertically, allow overflow if needed
		secondaryWidth = gridWidth + secondaryPadding * 2;
		secondaryHeight = gridHeight + secondaryPadding * 2;
	} else {
		// Square grid - white container is square
		secondaryWidth = gridWidth + secondaryPadding * 2;
		secondaryHeight = gridHeight + secondaryPadding * 2;
	}

	const gridStyle: React.CSSProperties = {
		gridTemplateColumns: `repeat(${currentWidth}, 1fr)`,
		gridTemplateRows: `repeat(${currentHeight}, 1fr)`,
		padding: `${secondaryPadding}px`,
		width: `${secondaryWidth}px`,
		height: `${secondaryHeight}px`,
		opacity: isAnimating ? 0.7 : 1,
	};

	return (
		<div className="gift-grid-container">
			<div
				className="gift-grid-wrapper"
				style={{
					width: `${primaryContainerSize}px`,
					height: `${primaryContainerSize}px`,
				}}
			>
				<div
					className={`gift-grid ${isAnimating ? "animating" : ""}`}
					style={gridStyle}
					data-testid="gift-grid"
				>
					{Array.from({ length: currentWidth * currentHeight }, (_, i) => (
						<div
							key={i}
							className="gift-grid-cell"
							data-testid="gift-grid-cell"
						/>
					))}
				</div>
			</div>
		</div>
	);
}

export default GiftGridVisual;
